import React, { useEffect, useMemo, useState } from 'react';
import { aiserverbaselink } from '../../../Env_Variables';
import { aiInstituteHeaders } from './aiInstituteHeaders';
import { formatCostEgp } from './usageDisplay';
import './OpenAiTracePanel.css';

function mergeTraces(prev, incoming) {
    if (!incoming?.length) return prev || [];
    const map = new Map((prev || []).map((t) => [t.id, t]));
    incoming.forEach((t) => {
        if (t?.id) map.set(t.id, t);
    });
    return [...map.values()].sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
}

function sumUsage(traces) {
    return (traces || []).reduce(
        (acc, t) => {
            acc.requests += 1;
            acc.prompt += Number(t.usage?.prompt_tokens) || 0;
            acc.completion += Number(t.usage?.completion_tokens) || 0;
            acc.cached += Number(t.usage?.cached_tokens) || 0;
            acc.reasoning += Number(t.usage?.reasoning_tokens) || 0;
            acc.tokens += Number(t.usage?.total_tokens) || 0;
            acc.cost += Number(t.usage?.cost_usd) || 0;
            return acc;
        },
        { requests: 0, prompt: 0, completion: 0, cached: 0, reasoning: 0, tokens: 0, cost: 0 },
    );
}

function groupTurns(traces) {
    const sorted = [...(traces || [])].sort((a, b) =>
        String(a.at || '').localeCompare(String(b.at || '')),
    );
    const map = new Map();
    let lastRealKey = null;

    sorted.forEach((t) => {
        const msg = String(t.user_message || '').trim();
        const placeholder = !msg || msg === '(tool_results)';
        let key = t.turn_id || '';
        if (!key) {
            key = placeholder && lastRealKey ? lastRealKey : `loose:${msg || t.id}`;
        }
        if (!placeholder) lastRealKey = key;
        else if (!t.turn_id && lastRealKey) key = lastRealKey;

        if (!map.has(key)) {
            map.set(key, {
                id: key,
                user_message: '',
                at: t.at || '',
                traces: [],
            });
        }
        const g = map.get(key);
        g.traces.push(t);
        if (t.at && (!g.at || String(t.at) > String(g.at))) g.at = t.at;
        if (!placeholder) g.user_message = msg;
    });

    return [...map.values()]
        .map((g) => ({ ...g, usage: sumUsage(g.traces) }))
        .sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
}

function tokenHeat(n) {
    const v = Number(n) || 0;
    if (v >= 6000) return 'is-hot';
    if (v >= 2000) return 'is-warm';
    return 'is-cool';
}

function prettyText(text) {
    const s = String(text || '');
    const t = s.trim();
    if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
        try {
            return JSON.stringify(JSON.parse(t), null, 2);
        } catch (_) {
            return s;
        }
    }
    return s;
}

function HideShow({ label, force, forceKey, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);

    useEffect(() => {
        if (force === 'open') setOpen(true);
        if (force === 'close') setOpen(false);
    }, [force, forceKey]);

    return (
        <div className="tbx-trace-hideshow">
            <button
                type="button"
                className="tbx-trace-toggle"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                {open ? `Hide ${label}` : `Show ${label}`}
            </button>
            {open ? children : null}
        </div>
    );
}

function MessageBlock({ message, index, force, forceKey, isRtl }) {
    const role = message.role || 'message';
    const body = prettyText(message.content);
    const chars = String(message.content || '').length;
    const toolCalls = Array.isArray(message.tool_calls) ? message.tool_calls : [];
    const label = message.tool_call_id
        ? `${role} #${index + 1} (${chars} chars, ${message.tool_call_id})`
        : `${role} #${index + 1} (${chars} chars)`;

    return (
        <div className={`tbx-trace-msg is-${role}`}>
            <HideShow label={label} force={force} forceKey={forceKey} defaultOpen={false}>
                {toolCalls.length > 0 && (
                    <div className="tbx-trace-toolcalls">
                        {toolCalls.map((tc, i) => (
                            <HideShow
                                key={tc.id || i}
                                label={isRtl ? `أدوات ${tc.name || 'tool'}` : `${tc.name || 'tool'} arguments`}
                                force={force}
                                forceKey={forceKey}
                            >
                                <pre className="tbx-trace-pre">{prettyText(tc.arguments || '')}</pre>
                            </HideShow>
                        ))}
                    </div>
                )}
                <pre className="tbx-trace-pre">{body || '—'}</pre>
            </HideShow>
        </div>
    );
}

function CallCard({ trace, index, total, force, forceKey, isRtl }) {
    const tokens = Number(trace.usage?.total_tokens) || 0;
    const prompt = Number(trace.usage?.prompt_tokens) || 0;
    const completion = Number(trace.usage?.completion_tokens) || 0;
    const cached = Number(trace.usage?.cached_tokens) || 0;
    const reasoning = Number(trace.usage?.reasoning_tokens) || 0;
    const cost = formatCostEgp(trace.usage);
    const ms = Number(trace.ms) || 0;
    const when = trace.at ? new Date(trace.at).toLocaleString() : '';
    const tools = Array.isArray(trace.tools) ? trace.tools : [];
    const messages = Array.isArray(trace.messages) ? trace.messages : [];

    return (
        <article className={`tbx-trace-card ${tokenHeat(prompt || tokens)}`}>
            <header className="tbx-trace-card-head">
                <div className="tbx-trace-card-title">
                    <span className="tbx-trace-num">
                        {index + 1}/{total}
                    </span>
                    <span className="tbx-trace-step">{trace.step || 'unknown'}</span>
                    {trace.stream ? <span className="tbx-trace-pill">stream</span> : null}
                </div>
                <div className={`tbx-trace-tokens ${tokenHeat(prompt || tokens)}`}>{tokens}</div>
            </header>
            <div className="tbx-trace-metrics">
                <span>prompt {prompt}</span>
                <span>·</span>
                <span>completion {completion}</span>
                {cached > 0 ? (
                    <>
                        <span>·</span>
                        <span>cached {cached}</span>
                    </>
                ) : null}
                {reasoning > 0 ? (
                    <>
                        <span>·</span>
                        <span>reasoning {reasoning}</span>
                    </>
                ) : null}
                <span>·</span>
                <span>{cost}</span>
                <span>·</span>
                <span>{ms}ms</span>
            </div>
            <div className="tbx-trace-meta">
                {when}
                {trace.tool_choice
                    ? ` · choice ${
                          typeof trace.tool_choice === 'string'
                              ? trace.tool_choice
                              : JSON.stringify(trace.tool_choice)
                      }`
                    : ''}
            </div>
            {tools.length > 0 && (
                <HideShow
                    label={isRtl ? `الأدوات (${tools.length})` : `tools (${tools.length})`}
                    force={force}
                    forceKey={forceKey}
                >
                    <div className="tbx-trace-tools">{tools.join(', ')}</div>
                </HideShow>
            )}
            <HideShow
                label={
                    isRtl
                        ? `البرومبت (${messages.length} رسالة)`
                        : `prompt (${messages.length} messages, ${trace.prompt_chars || 0} chars)`
                }
                force={force}
                forceKey={forceKey}
            >
                <div className="tbx-trace-messages">
                    {messages.map((m, i) => (
                        <MessageBlock
                            key={`${trace.id}-m-${i}`}
                            message={m}
                            index={i}
                            force={force}
                            forceKey={forceKey}
                            isRtl={isRtl}
                        />
                    ))}
                </div>
            </HideShow>
        </article>
    );
}

export default function OpenAiTracePanel({
    open,
    onClose,
    traces = [],
    onMergeTraces,
    instituteId,
    userId,
    conversationId,
    focusTurnId = null,
    isRtl,
}) {
    const [force, setForce] = useState('close');
    const [forceKey, setForceKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedTurnId, setSelectedTurnId] = useState(null);

    useEffect(() => {
        if (!open || !instituteId) return undefined;
        let cancelled = false;
        setLoading(true);
        const qs = conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : '';
        fetch(`${aiserverbaselink}/instowner/ai/openai-traces${qs}`, {
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                ...aiInstituteHeaders(instituteId, userId),
            },
        })
            .then((res) => (res.ok ? res.json() : { traces: [] }))
            .then((data) => {
                if (cancelled) return;
                if (Array.isArray(data.traces) && typeof onMergeTraces === 'function') {
                    onMergeTraces(data.traces);
                }
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, instituteId, userId, conversationId, onMergeTraces]);

    const turns = useMemo(() => groupTurns(traces), [traces]);
    const filteredTurns = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return turns;
        return turns.filter((turn) => String(turn.user_message || '').toLowerCase().includes(q));
    }, [turns, query]);

    useEffect(() => {
        if (!open) return;
        if (focusTurnId) {
            setSelectedTurnId(focusTurnId);
            return;
        }
        setSelectedTurnId(null);
    }, [open, focusTurnId]);

    const activeTurn =
        turns.find((t) => t.id === selectedTurnId) ||
        filteredTurns[0] ||
        turns[0] ||
        null;
    const activeTraces = activeTurn?.traces || [];
    const totals = activeTurn?.usage || sumUsage([]);

    if (!open) return null;

    return (
        <div className="tbx-trace-overlay" role="dialog" aria-label={isRtl ? 'طلبات OpenAI' : 'OpenAI requests'}>
            <div className="tbx-trace-panel">
                <header className="tbx-trace-head">
                    <div>
                        <div className="tbx-trace-title">{isRtl ? 'طلباتك' : 'Your questions'}</div>
                        <div className="tbx-trace-sub">
                            {loading
                                ? isRtl
                                    ? 'جارٍ التحميل…'
                                    : 'Loading…'
                                : isRtl
                                  ? 'اختار سؤال عشان تشوف طلبات OpenAI بتاعته بس.'
                                  : 'Pick a question to see only that request’s OpenAI calls.'}
                        </div>
                    </div>
                    <button type="button" className="tbx-trace-close" onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </header>
                <div className="tbx-trace-filter">
                    <input
                        className="tbx-trace-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={isRtl ? 'دور على سؤال…' : 'Filter by question…'}
                    />
                </div>
                <div className="tbx-trace-turns">
                    {filteredTurns.length === 0 ? (
                        <div className="tbx-trace-empty">
                            {isRtl
                                ? 'ابعت رسالة وبعدين هتظهر هنا.'
                                : 'Send a message, then pick it here to inspect that request only.'}
                        </div>
                    ) : (
                        filteredTurns.map((turn) => {
                            const selected = activeTurn?.id === turn.id;
                            return (
                                <button
                                    key={turn.id}
                                    type="button"
                                    className={`tbx-trace-turn ${selected ? 'is-selected' : ''} ${tokenHeat(
                                        turn.usage.prompt || turn.usage.tokens,
                                    )}`}
                                    onClick={() => setSelectedTurnId(turn.id)}
                                >
                                    <div className="tbx-trace-turn-q">
                                        {turn.user_message || (isRtl ? 'بدون نص' : '(no question text)')}
                                    </div>
                                    <div className="tbx-trace-turn-meta">
                                        {turn.usage.tokens} · {formatCostEgp(turn.usage.cost)}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
                {activeTurn ? (
                    <>
                        <div className="tbx-trace-actions">
                            <div className="tbx-trace-selected">
                                <div className="tbx-trace-selected-q">{activeTurn.user_message || '—'}</div>
                                <div className="tbx-trace-sub">
                                    {totals.tokens} · {formatCostEgp(totals.cost)}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="tbx-trace-toggle"
                                onClick={() => {
                                    setForce('open');
                                    setForceKey((k) => k + 1);
                                }}
                            >
                                {isRtl ? 'إظهار الكل' : 'Show all'}
                            </button>
                            <button
                                type="button"
                                className="tbx-trace-toggle"
                                onClick={() => {
                                    setForce('close');
                                    setForceKey((k) => k + 1);
                                }}
                            >
                                {isRtl ? 'إخفاء الكل' : 'Hide all'}
                            </button>
                        </div>
                        <div className="tbx-trace-body">
                            {activeTraces.map((trace, index) => (
                                <CallCard
                                    key={trace.id || index}
                                    trace={trace}
                                    index={index}
                                    total={activeTraces.length}
                                    force={force}
                                    forceKey={forceKey}
                                    isRtl={isRtl}
                                />
                            ))}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}

export { mergeTraces };
